// ============================================
// Dashboard de Renovaciones - MEJORADO
// /opt/dashboard-project/frontend/src/pages/finops/RenovacionesDashboard.js
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Calendar, DollarSign, Users, Download, ChevronDown, ChevronRight, AlertCircle, ExternalLink } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  fetchRenovacionesPorPeriodo,
  fetchRenovacionOportunidadServicios,
  fetchRenovacionesDashboardCompleto
} from '../../api';

export default function RenovacionesDashboard() {
  const [stats, setStats] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [renovacionesMesActual, setRenovacionesMesActual] = useState([]);
  const [topRenovaciones, setTopRenovaciones] = useState([]);
  const [distribucionNube, setDistribucionNube] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nubeFilter] = useState('');
  const [productoFilter] = useState('');
  const [isDarkMode] = useState(true);
  const [expandedOportunidades, setExpandedOportunidades] = useState({});
  const [serviciosDetalles, setServiciosDetalles] = useState({});
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Mapa de colores por nube
  const COLOR_MAP = {
    'Microsoft 365': '#00ffb3',
    'Microsoft Azure': '#04d9ff',
    'Amazon AWS': '#feca57',
    'Google GCP': '#667eea',
    'Google Workspace': '#f093fb',
    'Huawei Cloud': '#ff6b6b',
    'Ninguna': '#95afc0',
    'default': '#4ecdc4'
  };

  const getColorForNube = (nube) => {
    return COLOR_MAP[nube] || COLOR_MAP['default'];
  };

  // Scroll handler para ocultar/mostrar header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [nubeFilter, productoFilter]);

const loadData = async () => {
    const t0 = performance.now(); // Inicio del cronómetro
    setLoading(true);
    try {
      const filters = { nube_servicio: nubeFilter || undefined, producto: productoFilter || undefined };

      console.log("🚀 Iniciando carga de datos desde el Super Endpoint...");
      
      // LA MAGIA: Una sola llamada paralela
      const [dataCompleta, mesActualData] = await Promise.all([
        fetchRenovacionesDashboardCompleto(filters),
        fetchRenovacionesPorPeriodo('mes_actual', filters)
      ]);

      //logs para emdir tiempo de carga de data usando el super endpoint
      const t1 = performance.now(); // Fin del cronómetro
      console.log(`✅ Datos cargados exitosamente usando SUPER ENDPOINT.`);
      console.log(`⏱️ Tiempo de respuesta total: ${((t1 - t0) / 1000).toFixed(2)} segundos.`);

      // Repartir los datos
      setStats(dataCompleta.stats); 
      setTimeline(dataCompleta.timeline);
      setTopRenovaciones(dataCompleta.top); 
      setDistribucionNube(dataCompleta.distribucion);
      setRenovacionesMesActual(mesActualData.data || []);
      
    } catch (error) {
      console.error('Error loading renovaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => { setRefreshing(true); await loadData(); setRefreshing(false); };

  const handleExportCSV = () => {
    const csv = ['Cliente,Oportunidad,Fecha,Días,Servicios,Valor'].concat(
      renovacionesMesActual.map(i => [i.cliente, i.oportunidad, i.fecha_renovacion, i.dias_restantes, i.total_servicios, i.valor_total].join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'renovaciones.csv'; a.click();
  };

  const handleOpenAnalisis = () => {
    window.open('https://billinghub.kloud-pilot.com/renovaciones/', '_blank', 'noopener,noreferrer');
  };

  const toggleExpandOportunidad = async (oportunidad) => {
    const key = oportunidad.oportunidad;
    if (expandedOportunidades[key]) {
      setExpandedOportunidades(prev => ({ ...prev, [key]: false }));
    } else {
      setExpandedOportunidades(prev => ({ ...prev, [key]: true }));
      if (!serviciosDetalles[key]) {
        try {
          const servicios = await fetchRenovacionOportunidadServicios(key);
          setServiciosDetalles(prev => ({ ...prev, [key]: servicios }));
        } catch (error) {
          console.error(`Error loading servicios for ${key}:`, error);
        }
      }
    }
  };

  // Custom Tooltip para PieChart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a1a] border border-[#00ffb3] rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{payload[0].name}</p>
          <p className="text-[#00ffb3] text-sm">
            ${payload[0].value.toLocaleString()}
          </p>
          <p className="text-gray-400 text-xs">
            {payload[0].payload.porcentaje}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label para PieChart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null;

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const cardClasses = isDarkMode ? 'bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6' : 'bg-white border border-gray-200 rounded-xl p-6';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const themeClasses = isDarkMode ? 'bg-gradient-to-br from-[#001a14] via-[#002820] to-[#001410] text-white' : 'bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-50 text-gray-900';

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses} flex items-center justify-center`}>
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ffb3]"></div>
          <span className="text-xl font-semibold bg-gradient-to-r from-[#00ffb3] to-[#04d9ff] bg-clip-text text-transparent">Cargando renovaciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses}`}>
      {/* Header con animación de scroll */}
      <div 
        className={`sticky top-0 z-30 ${cardClasses} backdrop-blur-md transition-transform duration-300 ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="flex justify-between items-center px-6 py-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00ffb3] to-[#04d9ff] bg-clip-text text-transparent">
              Dashboard de Renovaciones
            </h1>
            <p className="text-lg opacity-80 mt-1">Gestión de Renovaciones</p>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={handleRefresh} disabled={refreshing}
              className={`p-2 rounded-lg transition-all ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleExportCSV}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              <Download className="w-4 h-4" />
              <span>Exportar</span>
            </button>
            <div className="relative rounded-lg p-[1.5px] overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.35),0_0_24px_rgba(0,255,179,0.28)]">
              <span className="pointer-events-none absolute inset-[-120%] bg-[conic-gradient(from_0deg,#a855f7,#00ffb3,#a855f7)] animate-[spin_3.5s_linear_infinite]" />
              <button
                onClick={handleOpenAnalisis}
                className="relative z-10 px-4 py-2 rounded-lg transition-all flex items-center gap-2 bg-[#0c2231] hover:bg-[#103047] text-white"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Analisis</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="px-6 py-8 space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className={cardClasses}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${textSecondary} text-sm`}>Total Renovaciones</p>
                <p className={`${textPrimary} text-3xl font-bold mt-2`}>{stats.total_oportunidades || 0}</p>
                <p className={`${textSecondary} text-xs mt-1`}>Próximas 3 meses</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${textSecondary} text-sm`}>Valor Total</p>
                <p className={`${textPrimary} text-3xl font-bold mt-2`}>${((stats.valor_total || 0) / 1000).toFixed(1)}k</p>
                <p className={`${textSecondary} text-xs mt-1`}>A renovar</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${textSecondary} text-sm`}>Este Mes</p>
                <p className={`${textPrimary} text-3xl font-bold mt-2`}>{stats.oportunidades_mes_actual || 0}</p>
                <p className={`${textSecondary} text-xs mt-1`}>Renovaciones urgentes</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-lg">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${textSecondary} text-sm`}>Clientes</p>
                <p className={`${textPrimary} text-3xl font-bold mt-2`}>{stats.total_clientes || 0}</p>
                <p className={`${textSecondary} text-xs mt-1`}>Con renovaciones</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline y Pie Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={`${cardClasses} lg:col-span-2`}>
            <h3 className={`${textPrimary} text-xl font-bold mb-4`}>Timeline de Renovaciones</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#00000020'} />
                <XAxis dataKey="mes_nombre" stroke={isDarkMode ? '#fff' : '#000'} />
                <YAxis stroke={isDarkMode ? '#fff' : '#000'} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#fff', 
                    border: '1px solid #00ffb3',
                    borderRadius: '8px'
                  }}
                  cursor={{ fill: 'rgba(0, 255, 179, 0.05)' }}
                />
                <Legend />
                <Bar dataKey="oportunidades" fill="#00ffb3" name="Oportunidades" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className={cardClasses}>
            <h3 className={`${textPrimary} text-xl font-bold mb-4`}>Distribución por Nube</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie 
                  data={distribucionNube} 
                  dataKey="valor" 
                  nameKey="nube_servicio" 
                  cx="50%" 
                  cy="50%" 
                  outerRadius={100}
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {distribucionNube.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColorForNube(entry.nube_servicio)} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            {/* Leyenda personalizada */}
            <div className="mt-4 space-y-2">
              {distribucionNube.map((entry, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getColorForNube(entry.nube_servicio) }}
                    ></div>
                    <span className={textSecondary}>{entry.nube_servicio}</span>
                  </div>
                  <span className={textPrimary}>${(entry.valor / 1000).toFixed(1)}k</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top 10 */}
        <div className={cardClasses}>
          <h3 className={`${textPrimary} text-xl font-bold mb-4`}>Top 10 Renovaciones Más Grandes</h3>
          <div className="space-y-3">
            {topRenovaciones.map((ren, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#00ffb3]/50 transition-colors">
                <div className="flex items-center gap-4 flex-1">
                  <span className="text-2xl font-bold text-[#00ffb3]">#{idx + 1}</span>
                  <div className="flex-1">
                    <p className={`${textPrimary} font-semibold`}>{ren.cliente}</p>
                    <p className={`${textSecondary} text-sm`}>Oport: {ren.oportunidad} • {ren.total_servicios} servicios</p>
                  </div>
                  <div className="text-right">
                    <p className={`${textPrimary} font-bold text-lg`}>${(ren.valor_total / 1000).toFixed(1)}k</p>
                    <p className={`${textSecondary} text-xs`}>{ren.dias_restantes} días</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabla Mejorada */}
        <div className={cardClasses}>
          <h3 className={`${textPrimary} text-xl font-bold mb-4`}>Renovaciones Urgentes (Este Mes)</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Cliente</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Oportunidad</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Fecha</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Días</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Servicios</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}>Valor</th>
                  <th className={`${textSecondary} text-left p-3 text-sm`}></th>
                </tr>
              </thead>
              <tbody>
                {renovacionesMesActual.map((ren, idx) => (
                  <React.Fragment key={idx}>
                    <tr className="border-b border-white/10 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => toggleExpandOportunidad(ren)}>
                      <td className={`${textPrimary} p-3 text-sm`}>{ren.cliente}</td>
                      <td className={`${textPrimary} p-3 text-sm font-mono`}>{ren.oportunidad}</td>
                      <td className={`${textSecondary} p-3 text-sm`}>{ren.fecha_renovacion}</td>
                      <td className={`${textPrimary} p-3 text-sm font-bold`}>
                        <span className={ren.dias_restantes <= 7 ? 'text-red-400' : ren.dias_restantes <= 15 ? 'text-yellow-400' : 'text-green-400'}>
                          {ren.dias_restantes}
                        </span>
                      </td>
                      <td className={`${textSecondary} p-3 text-sm`}>{ren.total_servicios}</td>
                      <td className={`${textPrimary} p-3 text-sm font-semibold`}>${ren.valor_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="p-3">
                        {expandedOportunidades[ren.oportunidad] ? <ChevronDown className="w-4 h-4 text-[#00ffb3]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </td>
                    </tr>
                    {expandedOportunidades[ren.oportunidad] && (
                      <tr>
                        <td colSpan="7" className="p-0">
                          <div className="bg-white/5 border-l-2 border-[#00ffb3]">
                            {serviciosDetalles[ren.oportunidad] ? (
                              <div className="p-4">
                                <div className="grid grid-cols-6 gap-2 mb-2 pb-2 border-b border-white/10">
                                  <div className={`${textSecondary} text-xs font-semibold uppercase`}>Servicio</div>
                                  <div className={`${textSecondary} text-xs font-semibold uppercase`}>Producto</div>
                                  <div className={`${textSecondary} text-xs font-semibold uppercase`}>Nube</div>
                                  <div className={`${textSecondary} text-xs font-semibold uppercase text-right`}>Cantidad</div>
                                  <div className={`${textSecondary} text-xs font-semibold uppercase text-right`}>Ingreso</div>
                                  <div className={`${textSecondary} text-xs font-semibold uppercase text-right`}>Costo</div>
                                </div>
                                <div className="space-y-2">
                                  {serviciosDetalles[ren.oportunidad].map((serv, sidx) => (
                                    <div key={sidx} className="grid grid-cols-6 gap-2 py-2 hover:bg-white/5 rounded px-2 transition-colors">
                                      <div className={`${textPrimary} text-xs truncate`} title={serv.servicio}>{serv.servicio}</div>
                                      <div className={`${textSecondary} text-xs truncate`} title={serv.producto}>{serv.producto || '-'}</div>
                                      <div className={`${textSecondary} text-xs truncate`} title={serv.nube_servicio}>{serv.nube_servicio || '-'}</div>
                                      <div className={`${textPrimary} text-xs text-right font-mono`}>{serv.cantidad || 1}</div>
                                      <div className={`${textPrimary} text-xs text-right font-mono font-semibold`}>${serv.ingreso != null ? serv.ingreso.toLocaleString() : '0.00'}</div>
                                      <div className={`${textSecondary} text-xs text-right font-mono`}>${serv.costo != null ? serv.costo.toLocaleString() : '0.00'}</div>
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <div className="grid grid-cols-6 gap-2">
                                    <div className="col-span-3"></div>
                                    <div className={`${textPrimary} text-xs font-bold text-right`}>TOTAL:</div>
                                    <div className={`${textPrimary} text-xs font-bold text-right`}>${serviciosDetalles[ren.oportunidad].reduce((sum, s) => sum + (s.ingreso || 0), 0).toLocaleString()}</div>
                                    <div className={`${textSecondary} text-xs font-bold text-right`}>${serviciosDetalles[ren.oportunidad].reduce((sum, s) => sum + (s.costo || 0), 0).toLocaleString()}</div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00ffb3]"></div>
                                  <span className={`${textSecondary} text-sm`}>Cargando servicios...</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {renovacionesMesActual.length === 0 && (
              <div className="text-center py-8">
                <p className={`${textSecondary} text-sm`}>No hay renovaciones urgentes este mes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}